import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Alert, AlertTitle, Link, Skeleton } from "@mui/material";
import * as React from "react";

import { useAnalytics, useNavigation } from "@/providers";
import { AddToCollection } from "@/shared/components/collections";
import { type SelectedResources } from "@/shared/components/collections/selectedResources";
import {
  DatasetsList,
  type DatasetRow,
} from "@/shared/components/datasets/DatasetsList";
import { EventsList } from "@/shared/components/events";
import { FilesList } from "@/shared/components/files";
import {
  type Filter,
  type FilterDefinition,
  SelectionOption,
} from "@/shared/components/ResourceView";
import {
  SessionsList,
  useCoreSessionFilters,
  useSessionCrossIdFilters,
} from "@/shared/components/sessions";
import { type CollectionResourceType } from "@/shared/domain/collections";
import { useUpdateCollection } from "@/shared/domain/collections/hooks/useUpdateCollection";
import {
  type SearchTablePreferenceKey,
  useSearchTablePreferences,
} from "@/shared/domain/config/hooks";
import { type DatasetRecord } from "@/shared/domain/datasets";
import { EventRecord, type EventQueryScope } from "@/shared/domain/events";
import { FileRecord, type FilesQueryScope } from "@/shared/domain/files";
import { SessionRecord } from "@/shared/domain/sessions";
import { Dialog, RobotoButton } from "@/shared/foundation/primitives";
import { usePopupState } from "@/shared/hooks/usePopupState";

import styles from "./CollectionItemsTab.module.css";
import { useCollectionOutletContext } from "./useCollectionOutletContext";

/**
 * Everything the Items tab needs in order to render one resource type.
 *
 * These three always change together, so they are one row per type rather
 * than parallel lists kept in the same order by hand. Adding a type to a set
 * of parallel lists and missing one of them rendered a blank tab rather than
 * failing to build, since the misses were a `default` arm and `&&`
 * conditionals; as a `Record` over the union it is a compile error instead.
 */
interface CollectionResourceView {
  /** Table-layout preferences to warm before the list mounts. */
  preferenceKey: SearchTablePreferenceKey;
  /** Body of the "Empty Collection" alert, phrased for this type. */
  emptyState: React.ReactNode;
  /** The list, plus any layout wrapper it needs to size correctly. */
  render: () => React.ReactNode;
}

/**
 * The type tag the "Add to Collection" dialog expects on its selection, for
 * each collection resource type.
 *
 * Typed as a `Record` over the union, so adding a collection resource type
 * fails to compile until it has an entry here.
 */
const SELECTED_RESOURCE_TYPES: Record<
  CollectionResourceType,
  SelectedResources["type"]
> = {
  dataset: "Dataset",
  file: "File",
  event: "Event",
  session: "Session",
};

/**
 * The rows a user had selected when they chose "Add to Collection".
 *
 * Set as the dialog opens and kept past its close: the dialog stays on
 * screen for its closing transition, and would otherwise read "0 datasets
 * selected" while it fades.
 */
interface PendingCollectionAdd {
  /** Ids of the selected members, all of the collection's resource type. */
  ids: string[];
  /** Deselects those rows in the list; called once the add succeeds. */
  clearSelection: () => void;
}

export const CollectionItemsTab: React.FC = () => {
  const collection = useCollectionOutletContext();

  const { goto } = useNavigation();
  const { trackEvent } = useAnalytics();

  const { mutate: updateCollection, error } = useUpdateCollection();

  const addToCollectionPopup = usePopupState();
  const { open: openPopup } = addToCollectionPopup;
  // While the dialog's writes are in flight it cannot be dismissed: they
  // cannot be cancelled, and a dismissal would drop their outcome.
  const [isAdding, setIsAdding] = React.useState(false);
  const [pendingAdd, setPendingAdd] =
    React.useState<PendingCollectionAdd | null>(null);
  const openAddToCollectionPopup = React.useCallback(
    (pending: PendingCollectionAdd) => {
      setPendingAdd(pending);
      openPopup();
    },
    [openPopup],
  );

  // Every member of this collection is already in it, so offering it as a
  // target would be a write that changes nothing.
  const excludedTargets = React.useMemo(
    () => [collection.collection_id],
    [collection.collection_id],
  );

  const eventScope: EventQueryScope = React.useMemo(
    () => ({ type: "collection", id: collection.collection_id }),
    [collection.collection_id],
  );

  const fileScope: FilesQueryScope = React.useMemo(
    () => ({ type: "collection", id: collection.collection_id }),
    [collection.collection_id],
  );

  const collectionScope: Filter[] = React.useMemo(
    () => [
      {
        type: "string",
        field: "collection.collection_id",
        comparator: "EQUALS",
        values: [collection.collection_id],
      },
    ],
    [collection.collection_id],
  );

  // Scope the identity filters' directories to the collection's owning org,
  // the same org the Items-tab mutations below write against.
  const coreFilters = useCoreSessionFilters(collection.org_id);
  const crossIdFilters = useSessionCrossIdFilters(collection.org_id);

  // Filter-menu surface for the Sessions view of a collection's Items tab.
  // The collection scope doesn't overlap with either cross-aggregate axis
  // below, so unlike the dataset- and device-scoped Sessions tabs (which each
  // omit their own pinned axis), both stay in -- matching the fully-unscoped
  // top-level `/sessions` page's filter set exactly.
  const collectionSessionsFilterDefinitions = React.useMemo<FilterDefinition[]>(
    () => [
      ...coreFilters,
      crossIdFilters.datasetIdFilter,
      crossIdFilters.deviceIdFilter,
    ],
    [coreFilters, crossIdFilters],
  );

  const datasetSelectionOptions: SelectionOption<DatasetRow>[] = React.useMemo(
    () => [
      {
        name: "Add to Collection",
        icon: <PlaylistAddIcon fontSize="small" />,
        action: ({ selected, clearSelection }) => {
          openAddToCollectionPopup({
            ids: selected.map((d) => d.dataset_id),
            clearSelection,
          });
        },
      },
      {
        name: "Remove from Collection",
        icon: <RemoveCircleOutlineIcon fontSize="small" />,
        action: ({ selected, clearSelection }) => {
          updateCollection(
            {
              collectionId: collection.collection_id,
              resourceOwnerId: collection.org_id,
              updates: {
                remove_resources: selected.map((d) => ({
                  resource_type: "dataset" as const,
                  resource_id: d.dataset_id,
                })),
              },
            },
            { onSuccess: clearSelection },
          );
        },
      },
      {
        name: "Visualize",
        icon: <VisibilityIcon fontSize="small" />,
        action: ({ selected }) => {
          goto.visualize({
            dataSource: { datasets: selected.map((d) => d.dataset_id) },
          });
        },
      },
    ],
    [
      collection.collection_id,
      collection.org_id,
      updateCollection,
      goto,
      openAddToCollectionPopup,
    ],
  );

  // The collection is fetched with `content_mode=full`, so `resources.dataset`
  // holds hydrated dataset records; refs the server could not resolve land in
  // `collection.missing` instead, and so stay out of "Visualize all".
  const collectionDatasetIds = React.useMemo(
    () =>
      ((collection.resources.dataset ?? []) as DatasetRecord[]).map(
        (dataset) => dataset.dataset_id,
      ),
    [collection.resources.dataset],
  );

  const datasetActions = (
    <RobotoButton
      variant="secondary"
      icon={<QueryStatsIcon aria-hidden="true" />}
      onClick={() => {
        trackEvent("VisualizeCollectionDatasetsClicked", {
          collectionId: collection.collection_id,
          datasetCount: collectionDatasetIds.length,
        });
        goto.visualize({ dataSource: { datasets: collectionDatasetIds } });
      }}
    >
      Visualize all
    </RobotoButton>
  );

  const eventSelectionOptions: SelectionOption<EventRecord>[] = React.useMemo(
    () => [
      {
        name: "Add to Collection",
        icon: <PlaylistAddIcon fontSize="small" />,
        action: ({ selected, clearSelection }) => {
          openAddToCollectionPopup({
            ids: selected.map((e) => e.event_id),
            clearSelection,
          });
        },
      },
      {
        name: "Remove from Collection",
        icon: <RemoveCircleOutlineIcon fontSize="small" />,
        action: ({ selected, clearSelection }) => {
          updateCollection(
            {
              collectionId: collection.collection_id,
              resourceOwnerId: collection.org_id,
              updates: {
                remove_resources: selected.map((e) => ({
                  resource_type: "event" as const,
                  resource_id: e.event_id,
                })),
              },
            },
            { onSuccess: clearSelection },
          );
        },
      },
    ],
    [
      collection.collection_id,
      collection.org_id,
      updateCollection,
      openAddToCollectionPopup,
    ],
  );

  const sessionSelectionOptions: SelectionOption<SessionRecord>[] =
    React.useMemo(
      () => [
        {
          name: "Add to Collection",
          icon: <PlaylistAddIcon fontSize="small" />,
          action: ({ selected, clearSelection }) => {
            openAddToCollectionPopup({
              ids: selected.map((s) => s.session_id),
              clearSelection,
            });
          },
        },
        {
          name: "Remove from Collection",
          icon: <RemoveCircleOutlineIcon fontSize="small" />,
          action: ({ selected, clearSelection }) => {
            updateCollection(
              {
                collectionId: collection.collection_id,
                resourceOwnerId: collection.org_id,
                updates: {
                  remove_resources: selected.map((s) => ({
                    resource_type: "session" as const,
                    resource_id: s.session_id,
                  })),
                },
              },
              { onSuccess: clearSelection },
            );
          },
        },
        {
          name: "Visualize",
          icon: <VisibilityIcon fontSize="small" />,
          action: ({ selected }) => {
            goto.visualize({
              dataSource: { sessions: selected.map((s) => s.session_id) },
            });
          },
        },
      ],
      [
        collection.collection_id,
        collection.org_id,
        updateCollection,
        goto,
        openAddToCollectionPopup,
      ],
    );

  // The collection is fetched with `content_mode=full`, so `resources.session`
  // holds hydrated session records; refs the server could not resolve land in
  // `collection.missing` instead, and so stay out of "Visualize all".
  const collectionSessionIds = React.useMemo(
    () =>
      ((collection.resources.session ?? []) as SessionRecord[]).map(
        (session) => session.session_id,
      ),
    [collection.resources.session],
  );

  const sessionActions = (
    <RobotoButton
      variant="secondary"
      icon={<QueryStatsIcon aria-hidden="true" />}
      onClick={() => {
        trackEvent("VisualizeCollectionSessionsClicked", {
          collectionId: collection.collection_id,
          sessionCount: collectionSessionIds.length,
        });
        goto.visualize({ dataSource: { sessions: collectionSessionIds } });
      }}
    >
      Visualize all
    </RobotoButton>
  );

  const fileSelectionOptions: SelectionOption<FileRecord>[] = React.useMemo(
    () => [
      {
        name: "Add to Collection",
        icon: <PlaylistAddIcon fontSize="small" />,
        action: ({ selected, clearSelection }) => {
          openAddToCollectionPopup({
            ids: selected.map((f) => f.file_id),
            clearSelection,
          });
        },
      },
      {
        name: "Remove from Collection",
        icon: <RemoveCircleOutlineIcon fontSize="small" />,
        action: ({ selected, clearSelection }) => {
          updateCollection(
            {
              collectionId: collection.collection_id,
              resourceOwnerId: collection.org_id,
              updates: {
                remove_resources: selected.map((f) => ({
                  resource_type: "file" as const,
                  resource_id: f.file_id,
                })),
              },
            },
            { onSuccess: clearSelection },
          );
        },
      },
      {
        name: "Visualize",
        icon: <VisibilityIcon fontSize="small" />,
        action: ({ selected }) => {
          goto.visualize({
            dataSource: { files: selected.map((f) => f.file_id) },
          });
        },
      },
    ],
    [
      collection.collection_id,
      collection.org_id,
      updateCollection,
      goto,
      openAddToCollectionPopup,
    ],
  );

  const resourceViews: Record<CollectionResourceType, CollectionResourceView> =
    {
      dataset: {
        preferenceKey: "dataset_search_table_preferences",
        emptyState: (
          <>
            There are no items in this collection yet. You can add{" "}
            <Link href="/datasets" underline="hover">
              datasets
            </Link>{" "}
            to it.
          </>
        ),
        render: () => (
          <DatasetsList
            scope={collectionScope}
            selectionOptions={datasetSelectionOptions}
            actions={datasetActions}
          />
        ),
      },
      file: {
        preferenceKey: "file_search_table_preferences",
        emptyState: (
          <>
            There are no items in this collection yet. You can add individual
            files to it.
          </>
        ),
        render: () => (
          <FilesList
            scope={fileScope}
            selectionOptions={fileSelectionOptions}
          />
        ),
      },
      event: {
        preferenceKey: "event_search_table_preferences",
        emptyState: (
          <>
            There are no items in this collection yet. You can add individual
            events to it.
          </>
        ),
        render: () => (
          <div className={styles.eventsTableWrapper}>
            <EventsList
              scope={eventScope}
              selectionOptions={eventSelectionOptions}
            />
          </div>
        ),
      },
      session: {
        preferenceKey: "session_search_table_preferences",
        emptyState: (
          <>
            There are no items in this collection yet. You can add individual
            sessions to it.
          </>
        ),
        render: () => (
          <SessionsList
            scope={collectionScope}
            selectionOptions={sessionSelectionOptions}
            filterDefinitions={collectionSessionsFilterDefinitions}
            actions={sessionActions}
          />
        ),
      },
    };

  // Undefined for a `resource_type` this build doesn't render yet -- e.g. a
  // collection created via the SDK/CLI ahead of the matching frontend release.
  // `resource_type` arrives through an unchecked response cast, so the lookup
  // can miss even though the `Record` is exhaustive over the union.
  const view: CollectionResourceView | undefined =
    resourceViews[collection.resource_type];

  const { isLoading: preferencesLoading } = useSearchTablePreferences(
    // Hooks can't be called conditionally: fall back to a harmless key for an
    // unsupported resource_type, since its result goes unused once the
    // component short-circuits below.
    view?.preferenceKey ?? "file_search_table_preferences",
  );

  if (preferencesLoading) {
    return (
      <Skeleton
        variant="rectangular"
        width="100%"
        height={300}
        style={{ marginTop: "var(--spacing-sm)" }}
      />
    );
  }

  if (view === undefined) {
    return (
      <Alert severity="info">
        <AlertTitle>Unsupported collection type</AlertTitle>
        This collection holds a resource type this version of the app
        doesn&apos;t support yet. Reload once the app updates, or use the CLI or
        SDK to view its contents.
      </Alert>
    );
  }

  const isEmpty =
    (collection.resources[collection.resource_type]?.length ?? 0) === 0;

  if (isEmpty) {
    return (
      <Alert severity="info">
        <AlertTitle>Empty Collection</AlertTitle>
        {view.emptyState}
      </Alert>
    );
  }

  return (
    <>
      {error && (
        <Alert severity="error" style={{ marginBottom: "var(--spacing-xs)" }}>
          {error.message}
        </Alert>
      )}

      {view.render()}

      <Dialog
        open={addToCollectionPopup.isOpen}
        onOpenChange={(open) => {
          if (!open && !isAdding) {
            addToCollectionPopup.close();
          }
        }}
      >
        <Dialog.Content
          // The panel renders its own heading as plain text, so the dialog
          // is named here.
          aria-label="Add to collection"
          className={styles.addToCollectionDialog}
          // The panel's search box takes focus itself as it mounts.
          initialFocus={false}
        >
          <AddToCollection
            selected={{
              type: SELECTED_RESOURCE_TYPES[collection.resource_type],
              items: new Set(pendingAdd?.ids ?? []),
            }}
            // The dialog lists and writes to the active org by default, which
            // is a different org from this collection's owner when a system
            // admin views another org's collection.
            orgId={collection.org_id}
            excludeCollectionIds={excludedTargets}
            onCancel={addToCollectionPopup.close}
            onSuccess={() => pendingAdd?.clearSelection()}
            onPendingChange={setIsAdding}
          />
        </Dialog.Content>
      </Dialog>
    </>
  );
};
